import { useState } from "react";
import { useJournalHistory } from "@/hooks/useJournalHistory";
import type { Journal } from "@vlab-corporation/vlab-pilates-service-interface/class";

const EQUIPMENT_LABELS: Record<string, string> = {
  reformer: "리포머",
  cadillac: "캐딜락",
  chair: "체어",
  barrel: "배럴",
  mat: "매트",
};

function getClassTypeLabel(classType: string | undefined, isOt: boolean | undefined): string {
  if (!classType) return "";
  if (classType === "PRIVATE_REGULAR") return isOt ? "1:1 OT" : "1:1 정규";
  if (classType === "GROUP_REGULAR") return isOt ? "그룹 OT" : "그룹 정규";
  return classType;
}

interface JournalHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  ownerUid: string;
  centerId: number;
  studiomateMemberId: number | undefined;
  memberName?: string;
}

function toDate(createdAt: unknown): Date | null {
  if (typeof createdAt === "string") {
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? null : d;
  }
  // Firestore Timestamp 객체 (serverTimestamp()로 저장된 데이터)
  if (createdAt && typeof createdAt === "object") {
    if ("toDate" in createdAt && typeof (createdAt as { toDate: unknown }).toDate === "function") {
      return (createdAt as { toDate(): Date }).toDate();
    }
    if ("seconds" in createdAt && typeof (createdAt as { seconds: unknown }).seconds === "number") {
      return new Date((createdAt as { seconds: number }).seconds * 1000);
    }
  }
  return null;
}

function formatDate(createdAt: unknown): string {
  try {
    const date = toDate(createdAt);
    if (!date) return "날짜 미상";
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const dow = days[date.getDay()];
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${m}/${d} (${dow}) ${hh}:${mm}`;
  } catch {
    return "날짜 미상";
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // clipboard unavailable — silent fail
      });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-xs text-primary-500 hover:text-primary-700 font-medium shrink-0 ml-2"
    >
      {copied ? "복사됨!" : "복사"}
    </button>
  );
}

function FieldRow({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-500">{label}</span>
        <CopyButton text={text} />
      </div>
      <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded p-2">
        {text}
      </p>
    </div>
  );
}

function JournalHistoryItem({ journal }: { journal: Journal }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="text-xs font-medium text-gray-700">
          {formatDate(journal.createdAt as unknown)}
        </span>
        <span className="text-gray-400 text-xs">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="p-3 space-y-3 bg-white">
          {journal.classType && (
            <div>
              <span className="text-xs font-semibold text-gray-500">수업 유형</span>
              <div className="mt-1">
                <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {getClassTypeLabel(journal.classType, journal.isOt)}
                </span>
              </div>
            </div>
          )}
          {journal.equipments.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-500">사용 기구</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {journal.equipments.map((eq) => (
                  <span
                    key={eq}
                    className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                  >
                    {EQUIPMENT_LABELS[eq] ?? eq}
                  </span>
                ))}
              </div>
            </div>
          )}
          {journal.exercises.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-500">수업 시퀀스</span>
              <ol className="mt-1 list-decimal list-inside space-y-0.5">
                {journal.exercises.map((ex, idx) => (
                  <li key={idx} className="text-xs text-gray-700">
                    <span>{ex.name}</span>
                    {ex.notes && <span className="text-gray-400 ml-1">· {ex.notes}</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {journal.problemList ? <FieldRow label="문제 목록" text={journal.problemList} /> : null}
          {journal.notes ? <FieldRow label="특이사항" text={journal.notes} /> : null}
          {journal.improvements ? <FieldRow label="개선된 점" text={journal.improvements} /> : null}
          {journal.improvementsNeeded ? (
            <FieldRow label="개선해야할 점" text={journal.improvementsNeeded} />
          ) : null}
          {!journal.classType &&
            !journal.equipments.length &&
            !journal.exercises.length &&
            !journal.problemList &&
            !journal.notes &&
            !journal.improvements &&
            !journal.improvementsNeeded && (
              <p className="text-xs text-gray-400">내용이 없습니다.</p>
            )}
        </div>
      )}
    </div>
  );
}

export function JournalHistoryDrawer({
  isOpen,
  onClose,
  ownerUid,
  centerId,
  studiomateMemberId,
  memberName,
}: JournalHistoryDrawerProps) {
  const {
    data: journals,
    isLoading,
    error,
  } = useJournalHistory(ownerUid, centerId, studiomateMemberId, memberName);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Bottom sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl max-h-[80vh] flex flex-col shadow-xl transition-transform duration-300 ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <h2 className="text-sm font-semibold text-gray-800">
            {memberName ? `${memberName}의 ` : ""}과거 일지
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-500 text-center py-10">불러오는 중...</p>}

          {!isLoading && error && (
            <p className="text-sm text-red-500 text-center py-10">일지를 불러오지 못했습니다.</p>
          )}

          {!isLoading && !error && (!journals || journals.length === 0) && (
            <p className="text-sm text-gray-400 text-center py-10">작성된 일지가 없습니다.</p>
          )}

          {!isLoading && !error && journals && journals.length > 0 && (
            <div className="space-y-2">
              {journals.map((journal) => (
                <JournalHistoryItem key={journal.id} journal={journal} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
